/**
 * 画布编辑器页面
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Canvas } from '../../components/canvas/Canvas';
import { ProjectProvider } from '../../components/canvas/providers/ProjectProvider';
import TourGuide from '../../components/TourGuide/TourGuide';
import { useAuth } from '../../components/auth/AuthContext';
import ApiService from '../../api/ApiService';
import LogService from '../../api/LogService';
import { toast } from 'react-toastify';
import { getVisualViewportBounds, visualViewportBoundsEqual } from './visualViewport';
import './CanvasEditor.css';

const apiService = new ApiService();

function CanvasEditor() {
  const { projectId: projectUuid } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { user: userInfo, openLogin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingTitle, setEditingTitle] = useState('');
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const defaultUpgradeMessage = '普通会员最多只能创建1个项目（包括克隆），请升级会员等级';
  const [showPublicDialog, setShowPublicDialog] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferringImages, setTransferringImages] = useState([]); // 记录未转存完成的图片信息
  const [runTour, setRunTour] = useState(false); // 控制引导是否运行
  const canvasContainerRef = useRef(null);
  const tourTimerRef = useRef(null); // 用于清理定时器
  const tourTriggeredRef = useRef(false); // 标记是否已触发过引导
  const [visualViewportBounds, setVisualViewportBounds] = useState(() => getVisualViewportBounds(
    typeof window !== 'undefined' ? window.visualViewport : null,
    {
      width: typeof window !== 'undefined' ? window.innerWidth : 0,
      height: typeof window !== 'undefined' ? window.innerHeight : 0,
    }
  ));

  // 本地存储键名 - 所有画布编辑器共用一个引导完成标记
  const TOUR_COMPLETED_KEY = 'canvas_tour_completed';

  useEffect(() => {
    const visualViewport = window.visualViewport;
    if (!visualViewport) {
      return undefined;
    }

    let animationFrameId = null;
    const syncVisualViewport = () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        const nextBounds = getVisualViewportBounds(visualViewport, {
          width: window.innerWidth,
          height: window.innerHeight,
        });
        setVisualViewportBounds(currentBounds => (
          visualViewportBoundsEqual(currentBounds, nextBounds) ? currentBounds : nextBounds
        ));
      });
    };

    syncVisualViewport();
    visualViewport.addEventListener('resize', syncVisualViewport);
    visualViewport.addEventListener('scroll', syncVisualViewport);

    return () => {
      visualViewport.removeEventListener('resize', syncVisualViewport);
      visualViewport.removeEventListener('scroll', syncVisualViewport);
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  // 提取错误信息的辅助函数
  const extractErrorMessage = (err, defaultMsg = '操作失败') => {
    if (!err) return defaultMsg;
    if (typeof err === 'string') return err;

    if (err.data) {
      if (typeof err.data === 'string') {
        try {
          const parsed = JSON.parse(err.data);
          if (parsed) {
            if (Array.isArray(parsed.detail)) {
              return parsed.detail.join('; ');
            }
            if (parsed.detail) {
              return parsed.detail;
            }
            if (parsed.message) {
              return parsed.message;
            }
            if (parsed.msg) {
              return parsed.msg;
            }
          }
        } catch (parseError) {
          if (err.data.trim()) {
            return err.data;
          }
        }
      } else if (typeof err.data === 'object' && err.data !== null) {
        if (Array.isArray(err.data.detail)) {
          return err.data.detail.join('; ');
        }
        return err.data.detail || err.data.message || err.data.msg || defaultMsg;
      }
    }

    if (err.message && typeof err.message === 'string') {
      return err.message;
    }

    return defaultMsg;
  };

  // 加载项目数据
  useEffect(() => {
    // 记录页面访问日志
    LogService.logPageView('画布编辑器', {
      pageCategory: '创作工具',
      pageType: '画布编辑页面',
      projectId: projectUuid,
      pageFeatures: ['节点编辑', '画布操作', '实时保存']
    });
    
    if (!projectUuid) {
      setError('项目ID无效');
      setLoading(false);
      return;
    }

    const loadProject = async () => {
      try {
        setLoading(true);
        setError(null);

        const isLoggedIn = Boolean(userInfo?.token);

        // 1. 获取项目基本信息
        console.log('正在加载项目信息...', projectUuid);
        const projectInfo = await apiService.getDrawProject(
          isLoggedIn ? userInfo.token : null,
          projectUuid
        );
        console.log('项目信息加载成功:', projectInfo);

        // 2. 判断是否只读模式
        // 未登录 或 别人的公开项目 都是只读
        const isOthersPublicProject = isLoggedIn && projectInfo.is_public && projectInfo.user_id !== userInfo.id;
        const shouldBeReadOnly = !isLoggedIn || isOthersPublicProject;
        setIsReadOnly(shouldBeReadOnly);
        
        if (!isLoggedIn) {
          console.log('未登录，进入只读模式');
        } else if (isOthersPublicProject) {
          console.log('这是别人的公开项目，进入只读模式');
        }

        // 3. 加载画布 JSON 数据
        let canvasData = null;
        if (projectInfo.canvas_url) {
          try {
            console.log('正在加载画布数据...');
            const canvasResponse = await apiService.loadDrawCanvas(
              isLoggedIn ? userInfo.token : null,
              projectUuid
            );
            console.log('画布数据加载成功:', canvasResponse);
            canvasData = canvasResponse.snapshot;
          } catch (canvasErr) {
            console.warn('加载画布数据失败，使用空画布:', canvasErr);
            // 如果加载失败，使用空画布
            canvasData = null;
          }
        }

        // 4. 构造项目对象
        // 如果画布为空，创建默认节点（GPT Image 2 + 文本输入 + 图片输入）
        let projectContent = canvasData || { nodes: [], edges: [] };
        
        if (!projectContent.nodes || projectContent.nodes.length === 0) {
          console.log('初始化默认画布节点...');
          projectContent = {
            nodes: [
              {
                id: "bRvufhKLER2kIFEdPaXIg",
                type: "gptImage2",
                position: {
                  x: 1118.0939509149596,
                  y: 343.8457720550851
                },
                data: {
                  resolution: "1K",
                  quality: "low",
                  outputFormat: "png",
                  aspectRatio: "4:3"
                },
                measured: {
                  width: 344,
                  height: 408
                },
                selected: false,
                dragging: false
              },
              {
                id: "38Nt83vAbkNoDt4072l4X",
                type: "promptInput",
                position: {
                  x: 581.9004726016431,
                  y: 337.20133604850093
                },
                data: {
                  prompt: "宫崎骏风格，女孩。"
                },
                measured: {
                  width: 356,
                  height: 260
                },
                selected: false,
                dragging: false
              },
              {
                id: "vXdvJJ9joMT_aHeXJaDGe",
                type: "imageReference",
                position: {
                  x: 583.6120019508787,
                  y: 694.1590308634707
                },
                data: {
                  imageUrl: ""
                },
                measured: {
                  width: 316,
                  height: 226
                },
                selected: false,
                dragging: false
              }
            ],
            edges: [
              {
                id: "ulu8uTdXmtyOVCwEr3pR7",
                source: "38Nt83vAbkNoDt4072l4X",
                target: "bRvufhKLER2kIFEdPaXIg",
                targetHandle: "prompt",
                type: "default"
              },
              {
                id: "wC5O48pGzB_1iAYd2oZog",
                source: "vXdvJJ9joMT_aHeXJaDGe",
                target: "bRvufhKLER2kIFEdPaXIg",
                targetHandle: "image",
                type: "default"
              }
            ]
          };
        }

        const loadedProject = {
          id: projectInfo.id,
          uuid: projectInfo.uuid,
          user_id: projectInfo.user_id,
          name: projectInfo.name,
          description: projectInfo.description,
          is_public: projectInfo.is_public,
          canvas_url: projectInfo.canvas_url,
          thumbnail_url: projectInfo.thumbnail_url,
          content: projectContent,
          created_at: projectInfo.created_at,
          updated_at: projectInfo.updated_at,
          last_accessed_at: projectInfo.last_accessed_at,
        };

        console.log('项目加载完成:', loadedProject);
        setProject(loadedProject);
        
        // 记录项目加载成功事件
        LogService.logCustomEvent('canvas_project_loaded', {
          projectId: projectUuid,
          projectName: loadedProject.name,
          isPublic: loadedProject.is_public,
          isReadOnly: shouldBeReadOnly,
          nodesCount: loadedProject.content?.nodes?.length || 0,
          edgesCount: loadedProject.content?.edges?.length || 0,
          actionResult: 'success',
          pageName: '画布编辑器'
        });
        
        setLoading(false);
      } catch (err) {
        console.error('加载项目失败:', err);
        const errorMessage = err?.data?.detail || err?.message || '加载项目失败';
        
        // 记录项目加载失败事件
        LogService.logCustomEvent('canvas_project_load_failed', {
          projectId: projectUuid,
          errorMessage: errorMessage,
          actionResult: 'error',
          pageName: '画布编辑器'
        });
        
        setError(errorMessage);
        toast.error(errorMessage);
        setLoading(false);
      }
    };

    loadProject();
  }, [projectUuid, history, userInfo?.id, userInfo?.token]);

  // 检查是否需要显示引导
  useEffect(() => {
    // 清理之前的定时器
    if (tourTimerRef.current) {
      clearTimeout(tourTimerRef.current);
      tourTimerRef.current = null;
    }

    // 只在首次加载完成且未触发过引导时检查
    if (!loading && project && !tourTriggeredRef.current) {
      // 检查是否已完成过引导（所有模式共用同一个标记）
      const tourCompleted = localStorage.getItem(TOUR_COMPLETED_KEY);

      // 如果用户未完成引导，自动开始
      if (!tourCompleted) {
        // 标记已触发，防止重复触发
        tourTriggeredRef.current = true;
        
        // 延迟一点时间，确保页面元素已渲染
        tourTimerRef.current = setTimeout(() => {
          setRunTour(true);

          // 记录引导开始事件
          LogService.logCustomEvent('canvas_tour_started', {
            projectId: projectUuid,
            isFirstTime: true,
            isReadOnly: isReadOnly,
            tourType: isReadOnly ? 'readonly' : 'edit',
            pageName: '画布编辑器'
          });
          
          tourTimerRef.current = null;
        }, 800);
      }
    }

    // 清理函数
    return () => {
      if (tourTimerRef.current) {
        clearTimeout(tourTimerRef.current);
        tourTimerRef.current = null;
      }
    };
  }, [loading, project, projectUuid, isReadOnly]);

  // 引导完成回调
  const handleTourFinish = useCallback((completed) => {
    // 清理可能存在的定时器
    if (tourTimerRef.current) {
      clearTimeout(tourTimerRef.current);
      tourTimerRef.current = null;
    }
    
    setRunTour(false);

    if (completed) {
      // 用户完整完成了引导，标记为已完成（所有模式共用同一个标记）
      localStorage.setItem(TOUR_COMPLETED_KEY, 'true');

      // 记录引导完成事件
      LogService.logCustomEvent('canvas_tour_completed', {
        projectId: projectUuid,
        completed: true,
        isReadOnly: isReadOnly,
        tourType: isReadOnly ? 'readonly' : 'edit',
        actionResult: 'success',
        pageName: '画布编辑器'
      });

      if (isReadOnly) {
        toast.success('欢迎浏览！复制项目后即可编辑');
      } else {
        toast.success('恭喜完成新手引导！');
      }
    } else {
      // 用户跳过了引导，也标记为已完成，避免下次再次弹出
      localStorage.setItem(TOUR_COMPLETED_KEY, 'true');

      LogService.logCustomEvent('canvas_tour_skipped', {
        projectId: projectUuid,
        completed: false,
        isReadOnly: isReadOnly,
        tourType: isReadOnly ? 'readonly' : 'edit',
        actionResult: 'skipped',
        pageName: '画布编辑器'
      });
    }
  }, [projectUuid, isReadOnly]);

  // 手动开始引导（帮助按钮）
  const handleStartTour = useCallback(() => {
    // 清理可能存在的定时器
    if (tourTimerRef.current) {
      clearTimeout(tourTimerRef.current);
      tourTimerRef.current = null;
    }
    
    setRunTour(true);

    // 记录手动开始引导事件
    LogService.logButtonClick('查看引导', 'help-tour-button', {
      projectId: projectUuid,
      actionType: '手动开启新手引导',
      pageName: '画布编辑器'
    });
  }, [projectUuid]);

  // 保存画布数据
  const handleSave = useCallback(
    async (nodes, edges) => {
      if (!projectUuid) {
        console.warn('项目ID不存在，跳过保存');
        return;
      }

      try {
        if (!userInfo?.token) {
          console.warn('未登录，跳过保存');
          return;
        }

        console.log('保存画布数据:', { 
          projectId: projectUuid, 
          nodesCount: nodes.length, 
          edgesCount: edges.length 
        });

        // 保存到后端
        await apiService.saveDrawCanvas(userInfo.token, projectUuid, {
          nodes,
          edges,
        });

        console.log('画布数据保存成功');

        // 更新本地状态
        setProject((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            content: { nodes, edges },
            updated_at: new Date().toISOString(),
          };
        });
      } catch (err) {
        console.error('保存失败:', err);
        const errorMessage = err?.data?.detail || err?.message || '保存失败';
        toast.error(errorMessage);
        throw err; // 重新抛出错误，让调用者知道保存失败
      }
    },
    [projectUuid, userInfo?.token]
  );

  // 手动保存
  const handleManualSave = async () => {
    if (!project) {
      toast.warning('项目数据未加载');
      return;
    }

    // 记录手动保存按钮点击日志
    LogService.logButtonClick('手动保存', 'manual-save-button', {
      projectId: projectUuid,
      projectName: project.name,
      nodesCount: project.content.nodes.length,
      edgesCount: project.content.edges.length,
      actionType: '手动保存画布',
      pageName: '画布编辑器'
    });

    try {
      setSaving(true);
      await handleSave(project.content.nodes, project.content.edges);
      
      // 记录保存成功事件
      LogService.logCustomEvent('canvas_manual_save_success', {
        projectId: projectUuid,
        nodesCount: project.content.nodes.length,
        edgesCount: project.content.edges.length,
        actionResult: 'success',
        pageName: '画布编辑器'
      });
      
      toast.success('保存成功');
    } catch (err) {
      console.error('手动保存失败:', err);
      
      // 记录保存失败事件
      LogService.logCustomEvent('canvas_manual_save_failed', {
        projectId: projectUuid,
        errorMessage: err?.message || '保存失败',
        actionResult: 'error',
        pageName: '画布编辑器'
      });
      
      // 错误提示已在 handleSave 中显示
    } finally {
      setSaving(false);
    }
  };

  // 复制项目（克隆）
  const handleCloneProject = async () => {
    if (!project || !userInfo) {
      openLogin();
      return;
    }

    // 记录克隆项目按钮点击日志
    LogService.logButtonClick('复制项目', 'clone-project-button', {
      projectId: projectUuid,
      projectName: project.name,
      isPublicProject: project.is_public,
      actionType: '克隆画布项目',
      pageName: '画布编辑器'
    });

    try {
      setCloning(true);
      console.log('开始克隆项目:', projectUuid);
      
      // 调用克隆接口
      const clonedProject = await apiService.cloneDrawProject(userInfo.token, projectUuid);
      console.log('克隆成功:', clonedProject);
      
      // 记录克隆成功事件
      LogService.logCustomEvent('canvas_project_cloned', {
        originalProjectId: projectUuid,
        clonedProjectId: clonedProject.uuid || clonedProject.id,
        projectName: project.name,
        actionResult: 'success',
        pageName: '画布编辑器'
      });
      
      toast.success('项目复制成功，即将跳转...');
      
      // 跳转到克隆后的项目
      const clonedIdentifier = clonedProject.uuid || clonedProject.id;
      if (!clonedIdentifier) {
        toast.error('克隆项目缺少唯一标识，无法打开');
        return;
      }

      setTimeout(() => {
        window.location.href = `/#/canvas-editor/${clonedIdentifier}`;
      }, 1000);
    } catch (err) {
      console.error('克隆项目失败:', err);
      const errorMsg = extractErrorMessage(err, '复制项目失败');
      const normalizedMessage = (errorMsg || '').trim();
      
      // 记录克隆失败事件
      LogService.logCustomEvent('canvas_project_clone_failed', {
        projectId: projectUuid,
        errorMessage: normalizedMessage,
        actionResult: 'error',
        pageName: '画布编辑器'
      });
      
      // 检查是否是会员限制错误
      if (err?.status === 403 || normalizedMessage.includes('升级会员') || normalizedMessage.includes('普通会员')) {
        const cleanedMessage = normalizedMessage.replace(/^复制项目失败[:：]?\s*/, '');
        setUpgradeMessage(cleanedMessage || defaultUpgradeMessage);
        setShowUpgradeModal(true);
      } else {
        toast.error(normalizedMessage || '复制项目失败');
      }
    } finally {
      setCloning(false);
    }
  };

  // 返回项目列表
  const handleBack = () => {
    // 读取 URL 查询参数中的 from 来源
    const searchParams = new URLSearchParams(location.search);
    const fromPage = searchParams.get('from');
    
    // 根据来源决定返回目标
    let targetPath = '/'; // 默认返回灵感广场
    if (fromPage === 'my-canvas') {
      targetPath = '/?tab=mine'; // 返回我的卡片
    }
    
    // 记录返回按钮点击日志
    LogService.logButtonClick('返回项目列表', 'back-to-list-button', {
      projectId: projectUuid,
      actionType: '离开画布编辑器',
      pageName: '画布编辑器',
      fromPage: fromPage || 'default',
      targetPath: targetPath
    });
    
    history.push(targetPath);
  };

  // 在当前画布上打开登录弹窗
  const handleGoToLogin = () => {
    const currentPath = window.location.hash.replace('#', '');
    openLogin(currentPath);
  };

  // 关闭升级弹窗
  const closeUpgradeModal = () => {
    setShowUpgradeModal(false);
  };

  // 跳转到会员页面
  const handleGoToMembership = () => {
    setShowUpgradeModal(false);
    window.open('https://apiz.ai/#/recharge', '_blank', 'noopener,noreferrer');
  };

  // 开始编辑标题
  const handleStartEditTitle = () => {
    setEditingTitle(project?.name || '');
    setIsEditingTitle(true);
  };

  // 保存标题
  const handleSaveTitle = async () => {
    if (!projectUuid || !editingTitle.trim()) {
      setIsEditingTitle(false);
      return;
    }

    if (editingTitle.trim() === project?.name) {
      setIsEditingTitle(false);
      return;
    }

    try {
      if (!userInfo?.token) {
        openLogin();
        setIsEditingTitle(false);
        return;
      }

      // 更新后端
      await apiService.updateDrawProject(userInfo.token, projectUuid, {
        name: editingTitle.trim()
      });

      setProject((prev) => {
        if (!prev) return null;
        return { ...prev, name: editingTitle.trim() };
      });
      setIsEditingTitle(false);
      toast.success('标题已更新');
    } catch (err) {
      console.error('更新标题失败:', err);
      const errorMessage = err?.data?.detail || err?.message || '更新标题失败';
      toast.error(errorMessage);
      setIsEditingTitle(false);
    }
  };

  // 取消编辑标题
  const handleCancelEditTitle = () => {
    setIsEditingTitle(false);
    setEditingTitle('');
  };

  // 处理标题输入框的键盘事件
  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      handleCancelEditTitle();
    }
  };

  // 处理分享链接
  const handleShareLink = async () => {
    if (!project) {
      toast.warning('项目数据未加载');
      return;
    }

    // 记录分享按钮点击日志
    LogService.logButtonClick('分享项目', 'share-project-button', {
      projectId: projectUuid,
      projectName: project.name,
      isPublic: project.is_public,
      actionType: '分享画布项目',
      pageName: '画布编辑器'
    });

    // 检查是否是公开项目
    if (!project.is_public) {
      setShowPublicDialog(true);
      return;
    }

    // 复制链接到剪贴板
    copyProjectLink();
  };

  // 复制项目链接
  const copyProjectLink = () => {
    const host = window.location.origin;
    const hash = window.location.hash.split('?')[0]; // 移除可能的查询参数
    const fullUrl = `${host}/${hash}`;
    
    navigator.clipboard.writeText(fullUrl).then(() => {
      // 记录链接复制成功事件
      LogService.logCustomEvent('canvas_link_copied', {
        projectId: projectUuid,
        projectName: project?.name,
        actionResult: 'success',
        pageName: '画布编辑器'
      });
      
      toast.success('链接已复制到剪贴板');
    }).catch(err => {
      console.error('复制失败:', err);
      
      // 记录链接复制失败事件
      LogService.logCustomEvent('canvas_link_copy_failed', {
        projectId: projectUuid,
        errorMessage: err.message,
        actionResult: 'error',
        pageName: '画布编辑器'
      });
      
      toast.error('复制失败，请手动复制');
    });
  };

  // 将项目设为公开并复制链接
  const handleMakePublicAndShare = async () => {
    try {
      if (!userInfo?.token) {
        openLogin();
        setShowPublicDialog(false);
        return;
      }

      // 更新项目为公开
      await apiService.updateDrawProject(userInfo.token, projectUuid, {
        is_public: true
      });

      // 更新本地状态
      setProject((prev) => {
        if (!prev) return null;
        return { ...prev, is_public: true };
      });

      setShowPublicDialog(false);
      toast.success('项目已设为公开');
      
      // 复制链接
      setTimeout(() => {
        copyProjectLink();
      }, 500);
    } catch (err) {
      console.error('设置公开失败:', err);
      const errorMessage = err?.data?.detail || err?.message || '设置公开失败';
      toast.error(errorMessage);
    }
  };

  // 检查是否有图片正在转存
  const checkImageTransferStatus = useCallback(() => {
    if (!project || !project.content || !project.content.nodes) {
      return { hasTransferring: false, transferringNodes: [] };
    }

    const transferringNodes = [];
    
    // 检查所有 ImageTaskNode 类型的节点
    project.content.nodes.forEach(node => {
      if (node.type === 'imageTask' && node.data) {
        const { status, generatedImages } = node.data;
        
        // 如果任务已完成且有生成的图片
        if (status === 'completed' && generatedImages && generatedImages.length > 0) {
          // 检查图片是否已经转存
          const hasUntransferredImages = generatedImages.some(img => {
            const url = typeof img === 'string' ? img : img.url;
            // 如果 URL 不是以 st-video.cc 开头，说明还未转存
            return url && !url.startsWith('https://st-video.cc') && !url.startsWith('https://cdn-video.51sux.com');
          });
          
          if (hasUntransferredImages) {
            transferringNodes.push({
              id: node.id,
              taskId: node.data.taskId,
              imageCount: generatedImages.length,
            });
          }
        }
      }
    });

    return {
      hasTransferring: transferringNodes.length > 0,
      transferringNodes,
    };
  }, [project]);

  const handleExportCanvas = useCallback(async () => {
    if (exporting) {
      return;
    }

    // 记录导出按钮点击日志
    LogService.logButtonClick('导出画布', 'export-canvas-button', {
      projectId: projectUuid,
      projectName: project?.name,
      nodesCount: project?.content?.nodes?.length || 0,
      edgesCount: project?.content?.edges?.length || 0,
      actionType: '导出画布为图片',
      pageName: '画布编辑器'
    });

    // 检查图片转存状态
    const { hasTransferring, transferringNodes } = checkImageTransferStatus();
    if (hasTransferring) {
      console.log('检测到图片正在转存，暂停导出:', transferringNodes);
      setTransferringImages(transferringNodes);
      setShowTransferDialog(true);
      return;
    }

    let cleanupTasks = [];

    const addCleanupTask = (task) => {
      if (typeof task === 'function') {
        cleanupTasks.push(task);
      }
    };

    const runCleanupTasks = () => {
      cleanupTasks.forEach((task) => {
        try {
          task();
        } catch (cleanupError) {
          console.warn('清理导出状态时出现问题:', cleanupError);
        }
      });
      cleanupTasks = [];
    };

    try {
      setExporting(true);

      if (!canvasContainerRef.current) {
        toast.error('画布尚未准备好');
        return;
      }

      const reactFlowRoot = canvasContainerRef.current.querySelector('.react-flow');

      if (!reactFlowRoot) {
        toast.error('未找到画布内容');
        return;
      }

      const bounds = reactFlowRoot.getBoundingClientRect();
      const contentWidth = Math.max(1, bounds.width);
      const contentHeight = Math.max(1, bounds.height);
      const minDimension = Math.min(contentWidth, contentHeight);
      const maxDimension = Math.max(contentWidth, contentHeight);

      const exportScale = Math.min(4, Math.max(1.8, 3200 / Math.max(240, minDimension)));
      const outputWidth = contentWidth * exportScale;
      const outputHeight = contentHeight * exportScale;

      const basePixelRatio = Math.max(3, (window.devicePixelRatio || 1) * 3.5);
      const pixelRatio = Math.min(10, basePixelRatio);

      // 在导出前，强制将边的样式内联，避免被裁剪
      const inlineEdgeStyles = () => {
        const edgePaths = reactFlowRoot.querySelectorAll('.react-flow__edge-path');
        if (!edgePaths.length) {
          return () => {};
        }

        const previousAttributes = Array.from(edgePaths).map((path) => ({
          element: path,
          stroke: path.getAttribute('stroke'),
          strokeWidth: path.getAttribute('stroke-width'),
          fill: path.getAttribute('fill'),
          vectorEffect: path.getAttribute('vector-effect'),
        }));

        edgePaths.forEach((path) => {
          const isSelected = !!path.closest('.react-flow__edge.selected');
          path.setAttribute('stroke', isSelected ? '#3b82f6' : '#667eea');
          path.setAttribute('stroke-width', isSelected ? '3' : '2');
          path.setAttribute('fill', 'none');
          path.setAttribute('vector-effect', 'non-scaling-stroke');
        });

        return () => {
          previousAttributes.forEach(({ element, stroke, strokeWidth, fill, vectorEffect }) => {
            const setOrRemove = (attr, value) => {
              if (value === null || value === undefined) {
                element.removeAttribute(attr);
              } else {
                element.setAttribute(attr, value);
              }
            };

            setOrRemove('stroke', stroke);
            setOrRemove('stroke-width', strokeWidth);
            setOrRemove('fill', fill);
            setOrRemove('vector-effect', vectorEffect);
          });
        };
      };

      const applyMediaPlaceholders = () => {
        const cleanupFns = [];

        const createPlaceholder = (element, type, options = {}) => {
          const rect = element.getBoundingClientRect();
          const placeholder = document.createElement('div');
          placeholder.setAttribute('data-canvas-export-placeholder', 'true');

          placeholder.style.display = 'flex';
          placeholder.style.alignItems = 'center';
          placeholder.style.justifyContent = 'center';
          placeholder.style.textAlign = 'center';
          placeholder.style.fontSize = '12px';
          placeholder.style.fontWeight = '500';
          placeholder.style.color = '#4b5563';
          placeholder.style.backgroundColor = '#f3f4f6';
          placeholder.style.border = '1px dashed #d1d5db';
          placeholder.style.borderRadius = window.getComputedStyle(element).borderRadius || '12px';
          placeholder.style.boxSizing = 'border-box';
          placeholder.style.width = `${rect.width}px`;
          placeholder.style.height = `${rect.height}px`;
          placeholder.style.overflow = 'hidden';
          placeholder.style.padding = '8px';

          if (options.backgroundImage) {
            placeholder.style.backgroundImage = `url(${options.backgroundImage})`;
            placeholder.style.backgroundSize = 'cover';
            placeholder.style.backgroundPosition = 'center';
            placeholder.style.color = '#111827';
            placeholder.style.textShadow = '0 1px 3px rgba(255,255,255,0.8)';
            placeholder.style.backdropFilter = 'blur(1px)';
            placeholder.style.border = '1px solid rgba(255,255,255,0.6)';
            placeholder.style.backgroundColor = '#f3f4f6';
          }

          placeholder.textContent = type === 'video' ? '视频内容预览不可导出' : '媒体内容预览不可导出';
          return placeholder;
        };

        const videos = reactFlowRoot.querySelectorAll('video');
        videos.forEach((video) => {
          if (!video.parentNode) {
            return;
          }

          const poster = video.getAttribute('poster');
          const placeholder = createPlaceholder(video, 'video', {
            backgroundImage: poster || undefined,
          });

          const previousDisplay = video.style.display;
          const previousVisibility = video.style.visibility;

          video.style.display = 'none';
          video.style.visibility = 'hidden';
          video.parentNode.insertBefore(placeholder, video);

          cleanupFns.push(() => {
            if (placeholder.parentNode) {
              placeholder.parentNode.removeChild(placeholder);
            }
            video.style.display = previousDisplay;
            video.style.visibility = previousVisibility;
          });
        });

        return () => {
          cleanupFns.forEach((fn) => {
            try {
              fn();
            } catch (cleanupError) {
              console.warn('清理媒体占位元素时出现问题:', cleanupError);
            }
          });
        };
      };

      // 在导出前预处理边样式，并在导出完成后恢复
      addCleanupTask(inlineEdgeStyles());
      addCleanupTask(applyMediaPlaceholders());

      const htmlToImageModule = await import('html-to-image');
      const { toPng } = htmlToImageModule;

      const dataUrl = await toPng(reactFlowRoot, {
        backgroundColor: '#ffffff',
        cacheBust: true,
        pixelRatio,
        filter: (node) => {
          if (!node || !(node instanceof Element)) {
            return true;
          }

          if (node.tagName && node.tagName.toLowerCase() === 'video') {
            return false;
          }

          const classes = node.classList || { contains: () => false };
          if (
            classes.contains('Canvas-mode-toggle') ||
            classes.contains('Canvas-save-indicator') ||
            classes.contains('ConnectionGuideEdge-guide-layer') ||
            classes.contains('react-flow__controls') ||
            classes.contains('react-flow__minimap') ||
            classes.contains('react-flow__panel')
          ) {
            return false;
          }

          return true;
        },
        width: outputWidth,
        height: outputHeight,
        style: {
          transform: `scale(${exportScale})`,
          transformOrigin: 'top left',
          width: `${contentWidth}px`,
          height: `${contentHeight}px`,
        },
      });

      const downloadLink = document.createElement('a');
      const sanitizedName = (project?.name || 'canvas')
        .replace(/[\\/:*?"<>|]+/g, '_')
        .trim() || 'canvas';
      const timestamp = new Date().toISOString().replace(/[-:]/g, '').replace('T', '_').slice(0, 15);
      downloadLink.href = dataUrl;
      downloadLink.download = `${sanitizedName}_${timestamp}.png`;
      downloadLink.click();
      
      // 记录导出成功事件
      LogService.logCustomEvent('canvas_exported', {
        projectId: projectUuid,
        projectName: project?.name,
        exportWidth: outputWidth,
        exportHeight: outputHeight,
        nodesCount: project?.content?.nodes?.length || 0,
        actionResult: 'success',
        pageName: '画布编辑器'
      });
      
      toast.success('画布已导出为高清图片');
    } catch (err) {
      console.error('导出画布失败:', err);
      
      // 记录导出失败事件
      LogService.logCustomEvent('canvas_export_failed', {
        projectId: projectUuid,
        errorMessage: err.message,
        actionResult: 'error',
        pageName: '画布编辑器'
      });
      
      toast.error('导出画布失败，请重试');
    } finally {
      runCleanupTasks();
      setExporting(false);
    }
  }, [exporting, project, checkImageTransferStatus, projectUuid]);

  if (loading) {
    return (
      <div className="CanvasEditor-loading">
        <div>加载项目中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="CanvasEditor-error">
        <div className="CanvasEditor-error-message">{error}</div>
        <button
          onClick={handleBack}
          className="CanvasEditor-error-button"
        >
          返回项目列表
        </button>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="CanvasEditor-loading">
        <div>项目不存在</div>
      </div>
    );
  }

  return (
    <div
      className="CanvasEditor-container"
      style={{
        top: visualViewportBounds.top,
        left: visualViewportBounds.left,
        right: 'auto',
        bottom: 'auto',
        width: visualViewportBounds.width,
        height: visualViewportBounds.height,
      }}
    >
      {/* 新手引导组件 */}
      <TourGuide run={runTour} isReadOnly={isReadOnly} onFinish={handleTourFinish} />

      {/* 顶部工具栏 */}
      <div className="CanvasEditor-toolbar canvas-editor-header">
        <div className="CanvasEditor-toolbar-left">
          <button onClick={handleBack} className="CanvasEditor-back-button">
            ← 返回
          </button>
          <div className="CanvasEditor-title-wrapper">
            {isEditingTitle && !isReadOnly ? (
              <input
                type="text"
                value={editingTitle}
                onChange={(e) => setEditingTitle(e.target.value)}
                onBlur={handleSaveTitle}
                onKeyDown={handleTitleKeyDown}
                autoFocus
                className="CanvasEditor-title-input"
              />
            ) : (
              <>
                <h2 className="CanvasEditor-title">{project.name}</h2>
                {!isReadOnly && (
                  <button
                    onClick={handleStartEditTitle}
                    className="CanvasEditor-edit-button"
                    title="编辑标题"
                  >
                    ✎
                  </button>
                )}
                {isReadOnly && (
                  <span className="CanvasEditor-readonly-badge">只读</span>
                )}
              </>
            )}
          </div>
        </div>
        <div className="CanvasEditor-toolbar-right">
          {/* 文档教程按钮 - 在所有模式下都显示 */}
          <button
            onClick={() => window.open('https://krxc4izye0.feishu.cn/wiki/J0nrwmKfki1WsIkRN7Qcdc6Kncc?from=from_copylink', '_blank')}
            className="CanvasEditor-help-button"
            title="查看文档教程"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 7h8M8 11h8M8 15h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* 帮助按钮 - 在所有模式下都显示 */}
          <button
            onClick={handleStartTour}
            className="CanvasEditor-help-button"
            title="查看新手引导"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 16v.01M12 12.5a2 2 0 10-2-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>

          {!userInfo?.token ? (
            // 未登录：显示登录按钮
            <button
              onClick={handleGoToLogin}
              className="CanvasEditor-login-button"
            >
              登录
            </button>
          ) : isReadOnly ? (
            // 已登录但只读：显示复制按钮
            <>
              {cloning && <span className="CanvasEditor-saving">复制中...</span>}
              <button
                onClick={handleCloneProject}
                disabled={cloning}
                className="CanvasEditor-clone-button"
              >
                {cloning ? '复制中...' : '📋 复制项目'}
              </button>
            </>
          ) : (
            // 已登录且可编辑：显示分享、导出和保存按钮
            <>
              <button
                onClick={handleShareLink}
                className="CanvasEditor-share-button"
                title="分享项目"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.5 10.5L21 3M21 3H15M21 3V9M10 5H7C5.89543 5 5 5.89543 5 7V17C5 18.1046 5.89543 19 7 19H17C18.1046 19 19 18.1046 19 17V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button
                onClick={handleExportCanvas}
                className="CanvasEditor-export-button"
                title="导出画布为图片"
                disabled={exporting}
              >
                {exporting ? (
                  <span className="CanvasEditor-export-spinner" />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 3V15M12 15L8 11M12 15L16 11M5 21H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
              {exporting && <span className="CanvasEditor-exporting">导出中...</span>}
              {saving && <span className="CanvasEditor-saving">保存中...</span>}
              <button
                onClick={handleManualSave}
                disabled={saving}
                className="CanvasEditor-save-button"
              >
                {saving ? '保存中...' : '保存'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* 画布区域 */}
      <div className="CanvasEditor-canvas" ref={canvasContainerRef}>
        <ProjectProvider project={project} readOnly={isReadOnly}>
          <Canvas
            projectId={projectUuid}
            initialNodes={project.content.nodes}
            initialEdges={project.content.edges}
            onSave={isReadOnly ? null : handleSave}
            readOnly={isReadOnly}
          />
        </ProjectProvider>
      </div>

      {/* 会员升级弹窗 */}
      {showUpgradeModal && (
        <div className="CanvasEditor-dialog-overlay CanvasEditor-upgrade-overlay" onClick={closeUpgradeModal}>
          <div
            className="CanvasEditor-dialog CanvasEditor-upgrade-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="CanvasEditor-upgrade-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L14.39 8.26L21 9.27L16 13.97L17.18 20.54L12 17.27L6.82 20.54L8 13.97L3 9.27L9.61 8.26L12 2Z" fill="currentColor" />
              </svg>
            </div>
            <h2 className="CanvasEditor-dialog-title CanvasEditor-upgrade-title">升级会员解锁更多创作空间</h2>
            <p className="CanvasEditor-upgrade-message">
              {upgradeMessage || defaultUpgradeMessage}
            </p>
            <ul className="CanvasEditor-upgrade-benefits">
              <li>非会员：最多创建1个项目</li>
              <li>VIP最多创建10个项目</li>
              <li>SVIP项目创建数量无限制</li>
            </ul>
            <div className="CanvasEditor-dialog-actions CanvasEditor-upgrade-actions">
              <button
                className="CanvasEditor-dialog-button CanvasEditor-dialog-button-cancel"
                onClick={closeUpgradeModal}
              >
                稍后再说
              </button>
              <button
                className="CanvasEditor-dialog-button CanvasEditor-upgrade-button"
                onClick={handleGoToMembership}
              >
                去开通会员
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 公开项目提示弹窗 */}
      {showPublicDialog && (
        <div className="CanvasEditor-dialog-overlay" onClick={() => setShowPublicDialog(false)}>
          <div
            className="CanvasEditor-dialog CanvasEditor-public-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="CanvasEditor-public-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 8V12L14.5 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <h2 className="CanvasEditor-dialog-title">分享项目需要设为公开</h2>
            <p className="CanvasEditor-public-message">
              此项目当前为私有状态，只有您可以访问。<br/>
              要分享项目链接，需要将项目设为公开。
            </p>
            <div className="CanvasEditor-dialog-actions">
              <button
                className="CanvasEditor-dialog-button CanvasEditor-dialog-button-cancel"
                onClick={() => setShowPublicDialog(false)}
              >
                取消
              </button>
              <button
                className="CanvasEditor-dialog-button CanvasEditor-dialog-button-confirm"
                onClick={handleMakePublicAndShare}
              >
                设为公开并复制链接
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 图片转存提示弹窗 */}
      {showTransferDialog && (
        <div className="CanvasEditor-dialog-overlay" onClick={() => setShowTransferDialog(false)}>
          <div
            className="CanvasEditor-dialog CanvasEditor-transfer-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="CanvasEditor-transfer-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <h2 className="CanvasEditor-dialog-title">请等待图片转存完成</h2>
            <p className="CanvasEditor-transfer-message">
              画布中有 {transferringImages.length} 个图片节点的图片正在转存到服务器，<br/>
              请稍等片刻后再导出图片。
            </p>
            <div className="CanvasEditor-transfer-details">
              <div className="CanvasEditor-transfer-tip">
                💡 提示：图片转存通常需要几秒到几十秒不等，转存完成后会自动更新。
              </div>
              {transferringImages.length > 0 && (
                <div className="CanvasEditor-transfer-list">
                  <div className="CanvasEditor-transfer-list-title">待转存的节点：</div>
                  {transferringImages.map((node, index) => (
                    <div key={node.id} className="CanvasEditor-transfer-item">
                      节点 {index + 1}：{node.imageCount} 张图片（任务ID: {node.taskId?.slice(0, 8)}...）
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="CanvasEditor-dialog-actions">
              <button
                className="CanvasEditor-dialog-button CanvasEditor-dialog-button-confirm"
                onClick={() => setShowTransferDialog(false)}
              >
                我知道了
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CanvasEditor;
